import type { DemoScript } from '../../../types/demo';

const AUDIO_BASE = 'https://bigsplice-demo-audio.s3.amazonaws.com/research';
const AUDIO_VERSION = 'v5';

/** Hardcoded demo file representing a sample research dataset */
const DEMO_FILE = {
  fileName: 'trial_results.csv',
  fileSize: 2850000,
  uploadDate: '2026-02-23T10:00:00Z',
  s3Key: 'users/demo/trial_results.csv',
  contentType: 'text/csv',
};

/** Mock schema for clinical trial results data */
const DEMO_SCHEMA = [
  { name: 'study_id', data_type: 'BYTE_ARRAY' },
  { name: 'title', data_type: 'BYTE_ARRAY' },
  { name: 'author', data_type: 'BYTE_ARRAY' },
  { name: 'findings', data_type: 'BYTE_ARRAY' },
  { name: 'p_value', data_type: 'DOUBLE' },
  { name: 'sample_size', data_type: 'INT64' },
  { name: 'methodology', data_type: 'BYTE_ARRAY' },
  { name: 'published_date', data_type: 'BYTE_ARRAY' },
];

/** Mock rows of clinical trial results */
const DEMO_ROWS = [
  { study_id: 'NCT-2025-0142', title: 'Phase III Efficacy of Compound MK-7 in Treatment-Resistant Depression', author: 'Dr. Sarah Chen', findings: 'Primary endpoint met with 43% reduction in HAM-D scores versus placebo (p<0.001). Sustained remission observed at 12-week follow-up in 61% of treated patients...', p_value: 0.0008, sample_size: 1240, methodology: 'Double-blind RCT', published_date: '2026-01-15' },
  { study_id: 'NCT-2024-1987', title: 'Longitudinal Outcomes of CAR-T Therapy in Pediatric ALL', author: 'Dr. James Rodriguez', findings: 'Five-year overall survival rate of 78.3%. Complete remission achieved in 92% of patients within 28 days. Late relapse observed in 14% of responders...', p_value: 0.0021, sample_size: 384, methodology: 'Prospective cohort', published_date: '2025-11-28' },
  { study_id: 'NCT-2025-0456', title: 'Gut Microbiome Diversity and Immunotherapy Response Correlation', author: 'Dr. Priya Patel', findings: 'Patients with high baseline microbiome diversity showed 2.3x higher objective response rate to anti-PD-1 therapy. Bacteroides fragilis abundance independently predicted response...', p_value: 0.0134, sample_size: 567, methodology: 'Cross-sectional analysis', published_date: '2026-02-01' },
  { study_id: 'NCT-2024-3301', title: 'Wearable Biomarker Monitoring for Early Sepsis Detection', author: 'Dr. Michael Torres', findings: 'Continuous heart rate variability monitoring detected sepsis onset 6.2 hours earlier than standard SIRS criteria. Sensitivity 89%, specificity 94% in ICU validation cohort...', p_value: 0.0003, sample_size: 2100, methodology: 'Diagnostic accuracy study', published_date: '2025-09-14' },
  { study_id: 'NCT-2025-0789', title: 'Efficacy Results of mRNA-Based Malaria Vaccine in Endemic Regions', author: 'Dr. Amina Okafor', findings: 'Vaccine efficacy of 71.4% against clinical malaria over 18 months. Robust T-cell response in 88% of participants. No serious adverse events related to vaccination...', p_value: 0.0001, sample_size: 4800, methodology: 'Phase III multi-center RCT', published_date: '2026-01-30' },
  { study_id: 'NCT-2025-1102', title: 'Sleep Architecture Changes with Novel Orexin Receptor Agonist', author: 'Dr. Elena Kowalski', findings: 'Significant increase in N3 deep sleep duration (+28 min, p=0.004). REM latency normalized in 73% of insomnia patients. No rebound insomnia observed upon discontinuation...', p_value: 0.0042, sample_size: 720, methodology: 'Double-blind RCT', published_date: '2026-02-10' },
];

/** Mock search results for "efficacy results" query */
const DEMO_SEARCH_RESULTS = [
  { study_id: 'NCT-2025-0142', title: 'Phase III Efficacy of Compound MK-7 in Treatment-Resistant Depression', findings: 'Primary endpoint met with 43% reduction in HAM-D scores versus placebo...', p_value: 0.0008, methodology: 'Double-blind RCT' },
  { study_id: 'NCT-2025-0789', title: 'Efficacy Results of mRNA-Based Malaria Vaccine in Endemic Regions', findings: 'Vaccine efficacy of 71.4% against clinical malaria over 18 months...', p_value: 0.0001, methodology: 'Phase III multi-center RCT' },
  { study_id: 'NCT-2025-1102', title: 'Sleep Architecture Changes with Novel Orexin Receptor Agonist', findings: 'Significant increase in N3 deep sleep duration. Efficacy results sustained at 12-week mark...', p_value: 0.0042, methodology: 'Double-blind RCT' },
];

/**
 * Research Pipeline demo script with LIVE DATA injection.
 * Uses hardcoded trial_results.csv with mock clinical trial data
 * flowing through all 5 nodes of the research flow template.
 */
export const researchScript: DemoScript = {
  id: 'research',
  templateId: 'research',
  name: 'Research Pipeline Demo',
  description: 'Learn how to upload, browse, and search research datasets with API access.',
  steps: [
    // === INTRO (no target) ===
    {
      id: 'intro',
      type: 'narrate',
      delayBefore: 300,
      subtitle: 'Hey there. Let me walk you through a research data pipeline \u2014 from raw datasets to full-text search and API access.',
      audio: { src: `${AUDIO_BASE}/intro.mp3?${AUDIO_VERSION}`, duration: 7000 },
    },

    // === STEP 1: Upload Dataset ===
    {
      id: 'upload-highlight',
      type: 'highlight',
      target: { nodeId: 'upload' },
      delayBefore: 200,
      subtitle: 'This is where your research data lands. Upload CSV files, Excel spreadsheets, or any structured dataset from your studies.',
      audio: { src: `${AUDIO_BASE}/upload-highlight.mp3?${AUDIO_VERSION}`, duration: 8000 },
      cursorPath: [
        { x: -80, y: -40, duration: 600 },
        { x: 0, y: 0, duration: 500 },
      ],
    },
    {
      id: 'upload-sample-note',
      type: 'narrate',
      target: { nodeId: 'upload' },
      subtitle: 'To keep things moving, this walkthrough uses simulated trial data. The pipeline itself is fully real \u2014 upload your own research files and it\u2019ll process them end to end.',
      audio: { src: `${AUDIO_BASE}/upload-sample-note.mp3?${AUDIO_VERSION}`, duration: 10500 },
    },
    {
      id: 'upload-decision',
      type: 'decision',
      target: { nodeId: 'upload' },
      subtitle: 'Want me to use our sample clinical trial dataset, or would you like to upload your own?',
      audio: { src: `${AUDIO_BASE}/upload-decision.mp3?${AUDIO_VERSION}`, duration: 4500 },
      decision: {
        prompt: 'Want me to use our sample dataset, or would you like to upload your own?',
        options: [
          { label: 'Use sample dataset', icon: 'beaker', nextStepId: 'upload-inject-uploading' },
          { label: 'I\u2019ll upload mine', icon: 'upload', nextStepId: 'upload-interact' },
        ],
        timeoutMs: 6000,
        defaultOptionIndex: 0,
      },
    },
    {
      id: 'upload-interact',
      type: 'interact',
      target: { nodeId: 'upload' },
      subtitle: 'Go ahead and drag a file onto the upload area. I\'ll wait for you.',
      audio: { src: `${AUDIO_BASE}/upload-interact.mp3?${AUDIO_VERSION}`, duration: 5500 },
      autoAdvanceAfter: 4000,
    },

    // === INJECT: Simulate file upload progress ===
    {
      id: 'upload-inject-uploading',
      type: 'inject',
      target: { nodeId: 'upload' },
      subtitle: 'Uploading file\u2026',
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'trial_results.csv', progress: 35 } },
      },
      autoAdvanceAfter: 250,
    },
    {
      id: 'upload-inject-progress',
      type: 'inject',
      target: { nodeId: 'upload' },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'trial_results.csv', progress: 72 } },
      },
      autoAdvanceAfter: 200,
    },
    {
      id: 'upload-inject-almost',
      type: 'inject',
      target: { nodeId: 'upload' },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'trial_results.csv', progress: 95 } },
      },
      autoAdvanceAfter: 150,
    },
    {
      id: 'upload-inject-done',
      type: 'inject',
      target: { nodeId: 'upload' },
      subtitle: 'Upload complete.',
      audio: { src: `${AUDIO_BASE}/upload-inject.mp3?${AUDIO_VERSION}`, duration: 1500 },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'done', fileName: 'trial_results.csv' } },
      },
    },

    // === STEP 2: View Data ===
    {
      id: 'dataset-highlight',
      type: 'highlight',
      target: { nodeId: 'sheets' },
      delayBefore: 150,
      subtitle: 'Now let\u2019s view the data and start exploring your research results.',
      audio: { src: `${AUDIO_BASE}/dataset-highlight.mp3?${AUDIO_VERSION}`, duration: 3500 },
      cursorPath: [
        { x: -40, y: -30, duration: 400 },
        { x: 0, y: 0, duration: 400 },
      ],
    },
    // Pre-inject mock data into downstream nodes BEFORE file selection triggers them
    {
      id: 'sheets-pre-inject',
      type: 'inject',
      inject: {
        nodeId: 'sheets',
        data: {
          demoState: {
            schema: DEMO_SCHEMA,
            rows: DEMO_ROWS,
            rowCount: 1247,
          },
        },
      },
      autoAdvanceAfter: 100,
    },
    {
      id: 'search-pre-inject',
      type: 'inject',
      inject: {
        nodeId: 'search',
        data: {
          demoState: {},
        },
      },
      autoAdvanceAfter: 100,
    },
    // Now inject the file \u2014 downstream nodes already have demoState and won\u2019t call APIs
    {
      id: 'dataset-inject',
      type: 'inject',
      target: { nodeId: 'sheets' },
      subtitle: 'Loading data\u2026 Watch how the data flows through the pipeline.',
      audio: { src: `${AUDIO_BASE}/dataset-selected.mp3?${AUDIO_VERSION}`, duration: 5500 },
      inject: {
        nodeId: 'sheets',
        data: { selectedFile: DEMO_FILE },
      },
    },

    // === STEP 3: Browse Data (mock data already loaded via demoState) ===
    {
      id: 'sheets-highlight',
      type: 'highlight',
      target: { nodeId: 'sheets' },
      delayBefore: 400,
      subtitle: 'Here\'s your data in spreadsheet form. Each row is a study \u2014 you can see the title, findings, p-values, sample sizes, and methodology.',
      audio: { src: `${AUDIO_BASE}/sheets-highlight.mp3?${AUDIO_VERSION}`, duration: 10000 },
      cursorPath: [
        { x: -100, y: -60, duration: 500 },
        { x: 0, y: 0, duration: 500 },
        { x: 50, y: 30, duration: 400 },
      ],
    },
    {
      id: 'sheets-decision',
      type: 'decision',
      target: { nodeId: 'sheets' },
      subtitle: '',
      decision: {
        prompt: 'Want to explore the data?',
        options: [
          { label: 'Explore the data', icon: 'search', nextStepId: 'sheets-interact' },
          { label: 'Show me search', icon: 'arrow-right', nextStepId: 'search-highlight' },
          { label: 'Skip to API', icon: 'skip-forward', nextStepId: 'api-highlight' },
        ],
        timeoutMs: 6000,
        defaultOptionIndex: 1,
      },
    },
    {
      id: 'sheets-interact',
      type: 'interact',
      target: { nodeId: 'sheets' },
      subtitle: 'Take a moment to browse the research data. Click any column header to sort by p-value, sample size, or publication date.',
      audio: { src: `${AUDIO_BASE}/sheets-interact.mp3?${AUDIO_VERSION}`, duration: 9000 },
      autoAdvanceAfter: 4000,
    },

    // === STEP 4: Full-Text Search ===
    {
      id: 'search-highlight',
      type: 'highlight',
      target: { nodeId: 'search' },
      subtitle: 'Full-text search across all your research data. Find specific findings, methodologies, or any keyword across thousands of studies in milliseconds.',
      audio: { src: `${AUDIO_BASE}/search-highlight.mp3?${AUDIO_VERSION}`, duration: 9000 },
      cursorPath: [
        { x: -60, y: -40, duration: 400 },
        { x: 0, y: 0, duration: 500 },
      ],
    },
    {
      id: 'search-decision',
      type: 'decision',
      target: { nodeId: 'search' },
      subtitle: '',
      decision: {
        prompt: 'Want to try a search?',
        options: [
          { label: 'Watch demo search', icon: 'play', nextStepId: 'search-demo' },
          { label: 'Try it myself', icon: 'keyboard', nextStepId: 'search-interact' },
        ],
        timeoutMs: 5000,
        defaultOptionIndex: 0,
      },
    },
    {
      id: 'search-demo',
      type: 'interact',
      target: { nodeId: 'search' },
      subtitle: 'Let me run a quick search \u2014 watch this.',
      audio: { src: `${AUDIO_BASE}/search-typing.mp3?${AUDIO_VERSION}`, duration: 2500 },
      interaction: { action: 'type', delay: 100, value: 'efficacy results' },
      autoAdvanceAfter: 1200,
    },
    // Inject mock search results after typing
    {
      id: 'search-inject-results',
      type: 'inject',
      target: { nodeId: 'search' },
      inject: {
        nodeId: 'search',
        data: {
          demoState: {
            results: DEMO_SEARCH_RESULTS,
            resultCount: 3,
          },
        },
      },
      autoAdvanceAfter: 1200,
    },
    {
      id: 'search-interact',
      type: 'interact',
      target: { nodeId: 'search' },
      subtitle: 'Type any search term into the search box. Try phrases like "double-blind RCT" or "treatment-resistant".',
      audio: { src: `${AUDIO_BASE}/search-interact.mp3?${AUDIO_VERSION}`, duration: 7500 },
      autoAdvanceAfter: 4000,
    },

    // === STEP 5: API Access ===
    {
      id: 'api-highlight',
      type: 'highlight',
      target: { nodeId: 'api-endpoint' },
      subtitle: 'Connect your research data to any tool via REST API \u2014 feed it into Claude for literature reviews, build custom dashboards, or integrate with your lab\u2019s analysis pipeline.',
      audio: { src: `${AUDIO_BASE}/api-highlight.mp3?${AUDIO_VERSION}`, duration: 11500 },
      cursorPath: [
        { x: -60, y: -30, duration: 500 },
        { x: 0, y: 0, duration: 400 },
      ],
    },

    // === ENDING ===
    {
      id: 'ending',
      type: 'narrate',
      subtitle: 'That\'s the complete pipeline \u2014 from raw research data to searchable results with API access for your AI tools. Ready to build your own?',
      audio: { src: `${AUDIO_BASE}/ending.mp3?${AUDIO_VERSION}`, duration: 9000 },
    },
    {
      id: 'ending-decision',
      type: 'decision',
      subtitle: '',
      decision: {
        prompt: 'What would you like to do next?',
        options: [
          { label: 'Start my own flow', icon: 'rocket', nextStepId: '__dismiss' },
          { label: 'Replay demo', icon: 'repeat', nextStepId: '__replay' },
          { label: 'Ask a question', icon: 'help-circle', nextStepId: '__ask' },
        ],
        timeoutMs: 0,
        defaultOptionIndex: -1,
      },
    },
  ],
};
